import React, { useEffect, useState } from "react";

/**
 * OAuthPopupCallback
 *
 * Generic landing page for OAuth integrations (gmail, calendar, youtube, instagram,
 * linkedin) that can be connected either the normal way (a same-tab visit
 * from the Integrations page) or from a popup window opened by the chat's
 * inline "Connect" card (see RunDrawer.jsx) — so the New Task conversation
 * never has to navigate away just to add an integration.
 *
 * The actual OAuth token exchange already happened server-side by the time
 * the browser lands here — the provider routes (routes/calendar.js,
 * routes/gmail.js, routes/auth.js's instagram/linkedin callbacks) redirect
 * here with the outcome already decided, passed via query params:
 *   ?provider=<id>&status=connected|error&msg=<optional error text>&fallback=<url-encoded original redirect target>
 *
 * If opened in a popup (window.opener is set), this posts the result back
 * to the opener and closes itself — mirroring FigmaOAuthCallback.tsx's
 * existing pattern for a different Figma OAuth flow. Otherwise (a normal
 * same-tab visit) it immediately redirects to `fallback`, so today's
 * existing same-tab behavior (landing on /integrations or /ai-console) is
 * completely unchanged for anyone not going through the chat popup path.
 */
export default function OAuthPopupCallback() {
  const [message, setMessage] = useState("Finishing up…");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get("provider") || "";
    const status = params.get("status") === "connected" ? "connected" : "error";
    const msg = params.get("msg") || "";
    const fallback = params.get("fallback") || "/integrations";

    const isPopup = !!window.opener && window.opener !== window;

    if (isPopup) {
      setMessage(status === "connected" ? `${provider || "Integration"} connected!` : `Couldn't connect: ${msg || "unknown error"}`);
      try {
        window.opener.postMessage({ type: "lyra_oauth", provider, status, msg }, window.location.origin);
      } catch (_) {
        // best effort — opener may already be gone
      }
      const t = setTimeout(() => window.close(), status === "connected" ? 900 : 2500);
      return () => clearTimeout(t);
    }

    // Not a popup — behave exactly like the direct redirect this replaced.
    window.location.replace(fallback);
  }, []);

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "var(--lyra-color-surface-2)", fontFamily: "'DM Sans', system-ui, sans-serif",
    }}>
      <div style={{
        textAlign: "center", padding: "32px 40px", background: "var(--lyra-color-surface)", borderRadius: 16,
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)", maxWidth: 380,
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--app-text-2)", lineHeight: 1.5 }}>{message}</div>
      </div>
    </div>
  );
}
